import _ from 'underscore';
import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import {
  mergeClasses,
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  useId,
  shorthands,
  useFocusFinders,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { SearchBox } from '@fluentui/react-search';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { Tags } from '@bingads-webui-fluent-component/tags';

import { SelectionFlatTree } from './selection-flat-tree';
import { ISelectionTreeProps } from '../types';

// offset for the popover due to the search icon
const popOverOffset = {
  crossAxis: -26,
  mainAxis: 1,
};

const useStyles = makeStyles({
  SearchBox: {
    width: '100%',
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    ...shorthands.padding(tokens.spacingVerticalL),
  },
  popOver: {
    boxSizing: 'border-box',
    ...shorthands.padding(tokens.spacingVerticalNone, tokens.spacingHorizontalNone),
  },
  screenReadersOnly: {
    position: 'absolute',
    width: tokens.strokeWidthThin,
    height: tokens.strokeWidthThin,
    ...shorthands.margin(`-${tokens.strokeWidthThin}`),
    ...shorthands.overflow('hidden'),
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
  },
});

export const SelectionTree = ({
  i18n,
  placeholder,
  items,
  openItems,
  searchQuery,
  itemsMap,
  checkedItems,
  defaultCheckedItems,
  updateOpenItems,
  updateSearchQuery,
  onChange,
  onRenderContent,
  onRenderTooltip,
  onRenderTagContent,
  customFilter,
  selectionMode,
  containerClassName,
  searchBoxClassName,
  treeClassName,
  showTags,
  tagViewLimit,
  isLoading,
  ...restProps
}: ISelectionTreeProps) => {
  const searchBoxId = useId('selection-tree-search-box-');
  const searchRef = useRef(null);
  const popOverRef = useRef(null);
  const { findFirstFocusable } = useFocusFinders();
  const [treeKey, setTreeKey] = useState('');
  const [open, setOpen] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const classes = useStyles();
  const [popoverMinWidth, setPopoverMinWidth] = React.useState(0);
  const [internalCheckedItems, setInternalCheckedItems] = useState(checkedItems || defaultCheckedItems);

  const tagItems = useMemo(() => {
    const tags = checkedItems || internalCheckedItems;
    if (!tags) {
      return [];
    }

    return [...tags].map((item) => {
      if (Array.isArray(item)) {
        if (item[1] === true) {
          return `${item[0]}`;
        }
        return undefined;
      }
      return `${item}`;
    }).filter((item) => itemsMap.has(item) && itemsMap.get(item).children.length === 0);
  }, [checkedItems || internalCheckedItems, itemsMap]);

  const onChangeSearchValue = useCallback((e, data) => {
    const trimmedValue = data.value.trim();
    updateSearchQuery(trimmedValue);
    setOpen(true);
  }, []);

  const onSearchKeyDown = useCallback((e) => {
    if (e.key === 'Tab' || e.key === 'ArrowDown') {
      e.preventDefault();
      const firstFocusable = findFirstFocusable(popOverRef.current);
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  }, [findFirstFocusable]);

  const handleOpenChange = useCallback((e, data) => {
    if (!e.currentTarget) {
      return;
    }

    const targetId = e.currentTarget.id;
    const isOpen = data.open || (targetId === searchBoxId && (e.type !== 'click' || !open));
    setOpen(isOpen);
  }, [searchBoxId, open]);

  const onTagChange = useCallback((newCheckedItems) => {
    onChange(newCheckedItems);
    if (!checkedItems) {
      // not controlled
      setTreeKey(_.uniqueId('tree-key-'));
    }
  }, [onChange, checkedItems]);

  useEffect(() => {
    if (searchRef.current && searchRef.current.parentElement && open) {
      setPopoverMinWidth(searchRef.current.parentElement.clientWidth);
    }
  }, [searchRef.current, open]);

  useEffect(() => {
    let message = '';
    if (isLoading) {
      message = i18n.getString(_TL_('Loading...'));
    } else if (searchQuery) {
      if (items.length === 0) {
        message = i18n.getString(_TL_('No results found'));
      } else {
        message = curlyTemplate(i18n.getString(_TL_('{{count}} results found')))({ count: items.length });
      }
    }
    setSearchMessage(message);
  }, [items.length, searchQuery]);

  return (
    <div className={mergeClasses('selection-tree', containerClassName)}>
      { showTags && (
        <Tags
          tagItems={tagItems}
          onChange={onTagChange}
          onRenderTagContent={onRenderTagContent ? (tag) => onRenderTagContent(itemsMap.get(tag)) : undefined}
          i18n={i18n}
          tagViewLimit={tagViewLimit}
        />
      ) }
      <Popover
        open={open}
        onOpenChange={handleOpenChange}
        unstable_disableAutoFocus
        trapFocus
        positioning={{
          target: searchRef.current,
          align: 'start',
          position: 'below',
          offset: popOverOffset,
        }}
      >
        <PopoverTrigger>
          <SearchBox
            className={mergeClasses('selection-tree-search-box', classes.SearchBox, searchBoxClassName)}
            ref={searchRef}
            id={searchBoxId}
            value={searchQuery}
            placeholder={placeholder}
            onChange={onChangeSearchValue}
            onKeyDown={onSearchKeyDown}
          />
        </PopoverTrigger>
        <PopoverSurface
          className={classes.popOver}
          style={{ minWidth: popoverMinWidth }}
          ref={popOverRef}
        >
          { isLoading ? (
            <Spinner size="small" label={i18n.getString(_TL_('Loading...'))} />
          ) : (
            <SelectionFlatTree
              {...restProps}
              i18n={i18n}
              key={treeKey}
              treeClassName={treeClassName}
              itemsMap={itemsMap}
              onChange={(checked) => { setInternalCheckedItems(checked); onChange(checked); }}
              customFilter={customFilter}
              onOpenChange={updateOpenItems}
              openItems={openItems}
              checkedItems={checkedItems}
              defaultCheckedItems={defaultCheckedItems}
              selectionMode={selectionMode}
              onRenderContent={onRenderContent}
              onRenderTooltip={onRenderTooltip}
              treeItems={items}
            />
          )}
        </PopoverSurface>
      </Popover>
      <div
        aria-live="polite"
        aria-atomic="true"
        className={classes.screenReadersOnly}
      >
        {searchMessage}
      </div>
    </div>
  );
};

SelectionTree.defaultProps = {
  placeholder: '',
  checkedItems: undefined,
  onChange: _.noop,
  onRenderContent: null,
  onRenderTooltip: null,
  onRenderTagContent: null,
  onRenderTagTooltip: null,
  showTags: true,
  customFilter: undefined,
  selectionMode: 'multiselect',
  searchBoxClassNames: '',
  tagViewLimit: 0,
};
